import React, { useState, useEffect } from "react"
import { PLATFORMS } from "../constants"
import { GetLanguageStatistics } from "aldoo-ra/Localization/api"
import Typography from 'aldoo-ra/Typography'
import Badge from 'aldoo-ra/Badge'

const ReviewStep = ({ formData, audiencePreview }) => {
  const [systemLanguages, setSystemLanguages] = useState([])

  useEffect(() => {
    loadSystemLanguages()
  }, [])

  const loadSystemLanguages = async () => {
    try {
      const response = await GetLanguageStatistics()
      if (response && response.languages) {
        // Transform system languages to match expected format
        const languages = response.languages.map(lang => ({
          value: lang.code,
          label: lang.name
        }))
        setSystemLanguages(languages)
      }
    } catch (error) {
      console.error('Failed to load system languages:', error)
      // Fallback to basic language codes if API fails
      setSystemLanguages([])
    }
  }

  const getPlatformLabel = (value) => {
    const platform = PLATFORMS.find(p => p.value === value)
    return platform ? platform.label : value
  }

  const getLanguageLabel = (value) => {
    const language = systemLanguages.find(l => l.value === value)
    return language ? language.label : value
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return "Not set"
    return new Date(dateString).toLocaleString()
  }

  const getFinalAudienceSize = () => {
    // audiencePreview.audienceSize already has audienceShare applied by backend
    return audiencePreview?.audienceSize || 0
  }


  return (
    <div className="space-y-4">
      {/* Newsletter Release Configuration - A4 Paper Report Layout */}
      <div className="bg-admin_paper shadow-lg border border-admin_bordered max-w-4xl mx-auto" style={{
        minHeight: '297mm', // A4 height
        width: '210mm', // A4 width
        padding: '20mm' // Standard margins
      }}>
        
        {/* Document Header */}
        <div className="text-center border-b-2 border-admin_bordered pb-4 mb-8">
          <h1 className="text-3xl font-bold mb-2 text-admin_text" style={{letterSpacing: '1px'}}>
            📄 NEWSLETTER RELEASE REPORT
          </h1>
          <div className="text-lg text-admin_text_secondary font-semibold">
            Configuration Summary & Release Details
          </div>
          <div className="text-sm text-admin_text_secondary mt-2">
            Generated on {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at {new Date().toLocaleTimeString()}
          </div>
        </div>

        {/* Condensed Report Content */}
        <div className="space-y-6 text-sm leading-relaxed">

          {/* Release Summary */}
          <div className="bg-admin_paper p-4 rounded-lg border border-admin_bordered">
            <h3 className="font-bold text-admin_text mb-3 text-base">📋 RELEASE SUMMARY</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-admin_text_secondary text-sm">Name:</span>
                <div className="font-semibold text-admin_text">{formData.name || "Untitled Release"}</div>
              </div>
              <div>
                <span className="text-admin_text_secondary text-sm">Scheduled:</span>
                <div className="font-semibold text-admin_text">{formatDateTime(formData.scheduledAt)}</div>
              </div>
              <div className="col-span-2">
                <span className="text-admin_text_secondary text-sm">Recipients:</span>
                <div className="font-semibold text-admin_text">
                  {getFinalAudienceSize().toLocaleString()} subscribers
                </div>
              </div>
            </div>
            {formData.description && (
              <div className="mt-3 pt-3 border-t border-admin_bordered">
                <span className="text-admin_text_secondary text-sm">Description:</span>
                <div className="mt-1 italic text-admin_text">{formData.description}</div>
              </div>
            )}
          </div>

          {/* Email Templates */}
          <div className="bg-admin_paper p-4 rounded-lg border border-admin_bordered">
            <h3 className="font-bold text-admin_text mb-3 text-base">✉️ EMAIL TEMPLATES</h3>
            {formData.templates.length === 0 ? (
              <p className="text-error">⚠️ No templates selected</p>
            ) : (
              <div className="space-y-2">
                {formData.templates.map((template, index) => (
                  <div key={index} className="flex items-center justify-between bg-admin_canvas p-2 rounded">
                    <span className="font-medium text-admin_text">{template.templateName || "Unknown Template"}</span>
                    <span className="text-admin_text font-semibold">{template.percentage}%</span>
                  </div>
                ))}
                {formData.templates.length > 1 && (
                  <p className="text-success text-xs mt-2 italic">
                    ✓ A/B Testing enabled - Recipients randomly distributed
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Audience Details */}
          <div className="bg-admin_paper p-4 rounded-lg border border-admin_bordered">
            <h3 className="font-bold text-admin_text mb-3 text-base">🎯 AUDIENCE TARGETING</h3>
            <div className="space-y-2">
              <div className="flex items-start">
                <span className="text-admin_text_secondary text-sm min-w-[140px]">Segments:</span>
                <span className="font-medium text-admin_text">{formData.segments.length === 0 ? 'All Subscribers' : formData.segments.map(s => s.replace('all-subscribers', 'All Subscribers')).join(', ')}</span>
              </div>
              <div className="flex items-start">
                <span className="text-admin_text_secondary text-sm min-w-[140px]">Languages:</span>
                <span className="font-medium text-admin_text">
                  {formData.targetLanguagesEnabled
                    ? formData.targetLanguages.map(lang => getLanguageLabel(lang)).join(', ')
                    : 'All languages'}
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-admin_text_secondary text-sm min-w-[140px]">Platforms:</span>
                <span className="font-medium text-admin_text">
                  {formData.targetPlatformsEnabled
                    ? formData.targetPlatforms.map(platform => getPlatformLabel(platform)).join(', ')
                    : 'All platforms'}
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-admin_text_secondary text-sm min-w-[140px]">Audience Share:</span>
                <span className="font-semibold text-admin_text">{formData.audienceShareEnabled ? (formData.audienceShare || 100) : 100}%</span>
              </div>
              <div className="flex items-start">
                <span className="text-admin_text_secondary text-sm min-w-[140px]">User LTV:</span>
                <span className="font-medium text-admin_text">
                  {formData.ltvFilterEnabled ? `${formData.ltvOperator === 'gte' ? '≥' : '≤'} $${formData.ltvAmount || 0}` : 'Not applied'}
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-admin_text_secondary text-sm min-w-[140px]">Include Unregistered:</span>
                <span className={`font-medium ${formData.includeUnregistered ? 'text-admin_success' : 'text-admin_error'}`}>
                  {formData.includeUnregistered ? '✓ Yes' : '✗ No'}
                </span>
              </div>
            </div>
          </div>

          {/* Audience Breakdown */}
          {audiencePreview?.breakdown && (
            <div className="bg-admin_paper p-4 rounded-lg border border-admin_bordered">
              <h3 className="font-bold text-admin_text mb-3 text-base">📊 AUDIENCE BREAKDOWN</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* User Types */}
                <div className="flex flex-col h-full justify-between bg-admin_canvas rounded-lg p-3">
                  <Typography variant="admin_label" className="mb-2">
                    User Types
                  </Typography>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1_dim">
                        Registered
                      </Typography>
                      <Badge variant="success" size="sm">
                        {(audiencePreview.breakdown.registered || 0).toLocaleString()}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between">
                      <Typography variant="admin_txt1_dim">
                        Anonymous
                      </Typography>
                      <Badge variant="admin_default" size="sm">
                        {(audiencePreview.breakdown.anonymous || 0).toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Languages */}
                {audiencePreview.breakdown?.languages && Object.keys(audiencePreview.breakdown.languages).length > 0 && (
                  <div className="flex flex-col h-full justify-between bg-admin_canvas rounded-lg p-3">
                    <Typography variant="admin_label" className="mb-2">
                      Languages
                    </Typography>

                    <div className="space-y-1">
                      {Object.entries(audiencePreview.breakdown.languages)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 3)
                        .map(([lang, count]) => (
                        <div key={lang} className="flex items-center justify-between">
                          <Typography variant="admin_txt1_dim" className="uppercase">
                            {lang}
                          </Typography>
                          <Badge variant="info" size="sm">
                            {count.toLocaleString()}
                          </Badge>
                        </div>
                      ))}

                      {Object.keys(audiencePreview.breakdown.languages).length > 3 && (
                        <div className="flex justify-center pt-1">
                          <Badge variant="info" size="sm">
                            +{Object.keys(audiencePreview.breakdown.languages).length - 3} more
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Platforms */}
                {audiencePreview.breakdown?.platforms && Object.keys(audiencePreview.breakdown.platforms).length > 0 && (
                  <div className="flex flex-col h-full justify-between bg-admin_canvas rounded-lg p-3">
                    <Typography variant="admin_label" className="mb-2">
                      Platforms
                    </Typography>

                    <div className="space-y-1">
                      {Object.entries(audiencePreview.breakdown.platforms)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 3)
                        .map(([platform, count]) => (
                        <div key={platform} className="flex items-center justify-between">
                          <Typography variant="admin_txt1_dim" className="capitalize">
                            {platform === 'unknown' ? 'Unknown' : platform}
                          </Typography>
                          <Badge variant="warning" size="sm">
                            {count.toLocaleString()}
                          </Badge>
                        </div>
                      ))}

                      {Object.keys(audiencePreview.breakdown.platforms).length > 3 && (
                        <div className="flex justify-center pt-1">
                          <Badge variant="warning" size="sm">
                            +{Object.keys(audiencePreview.breakdown.platforms).length - 3} more
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

      </div>


    </div>
  )
}

export default ReviewStep